import { ChangeDetectionStrategy, Component, DoCheck, ElementRef, Injector, OnInit, afterNextRender, computed, inject, input, signal } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule, ɵgetFieldValue as getFieldValue } from '@ngx-formly/core';
import { RevealMenuComponent } from '../reveal-menu/reveal-menu.component';
import { RevealPanelComponent } from '../reveal-menu/reveal-panel.component';
import type { RevealItem, RevealState } from '../reveal-menu/reveal-menu.types';
import { chipText, isFilled, opensInPanel } from '../reveal-menu/reveal.util';
import { hasHiddenClass } from './readonly/overview.types';
import { commitUserValue, markUserEdit } from './user-edit.util';

/**
 * A value setValue accepts: a group needs every child present and not undefined (NG01002), so gaps
 * are filled with empties — null for a leaf, [] for an array.
 */
function complete(control: AbstractControl, value: unknown): unknown {
  if (control instanceof FormGroup) {
    const record = (value ?? {}) as Record<string, unknown>;
    return Object.fromEntries(Object.entries(control.controls).map(([key, child]) => [key, complete(child, record[key])]));
  }
  if (control instanceof FormArray) return Array.isArray(value) ? value : [];
  return value ?? null;
}

/** The empty value of a control. */
const blank = (control: AbstractControl): unknown => complete(control, undefined);

/** Fields already warned about, so a recompute does not repeat the console.warn. */
const warned = new WeakSet<FormlyFieldConfig>();

/** Fields this component hid itself, so a rebuild does not mistake them for admin-hidden ones. */
const revealHidden = new WeakSet<FormlyFieldConfig>();

interface Entry {
  field: FormlyFieldConfig;
  key: string;
  reveal: boolean;
  panel: boolean;
  /** Set on a rider (`props.revealWith`): the key of the host entry whose panel it rides in. */
  host: string | null;
  /** On a host: the fields that ride in its panel. */
  riders: FormlyFieldConfig[];
}

/** A control's value as it was when a panel opened. */
interface Snapshot {
  control: AbstractControl;
  value: unknown;
  pristine: boolean;
}

/** Hidden for this role (`hideUserRole` sets the `hidden` class, not Formly `hide`, so the value still saves). */
const roleHidden = (field: FormlyFieldConfig): boolean => hasHiddenClass(field.className);

/**
 * No edit right for this role (Formly carries `props.disabled` down the tree): readable, not changeable.
 * The parent too: a field behind the menu has no registered control, so a disabled form misses it.
 */
const locked = (field: FormlyFieldConfig): boolean =>
  !!(field.props?.disabled || field.formControl?.disabled || field.parent?.formControl?.disabled);

/** A repeat anywhere inside: Annuleren could not restore its rows (setValue throws NG01002 once the row count changed). */
const holdsRepeat = (field: FormlyFieldConfig): boolean => !!field.fieldArray || (field.fieldGroup ?? []).some(holdsRepeat);

const matches = (key: string, listed: string): boolean => key === listed || key.split('.').pop() === listed;

/**
 * A panel field that fills itself from a sibling (linked-value `sourceFieldKey`: the stop note takes
 * the picked address's instructions). Formly-hiding it would destroy the component that listens, so
 * behind the menu it stays registered and only folded (display: none).
 */
// A required one is left out: registered but folded away, it would hold the form invalid with nothing
// on screen to fill in, so it keeps the normal hide (and stays empty until picked).
const fillsItself = (field: FormlyFieldConfig): boolean =>
  !!field.props?.['sourceFieldKey'] && !field.props?.required && opensInPanel(field);

/**
 * Draws a list of fields where some sit behind a "Toevoegen" menu (`revealKeys`): a stop's note,
 * contact person and volumes, or the flat note fields of a route section's departure slot.
 *
 * Hidden ≠ removed: a field that is not picked is Formly-hidden (it is empty then, so losing its
 * control costs nothing); a filled text field folded into a chip stays rendered and registered and
 * is only collapsed, because Formly's `hide` takes the control out of the form even with
 * `resetOnHide: false`. The fieldGroup is never spliced (a repeat's rows go through a rebuild).
 *
 * States are decided in ngOnInit and again whenever the row's model object changes (a reorder moves
 * the models through the slots). A self-filling field (`fillsItself`) follows its own value on every
 * check. Known limit: a value that lands in any other still-hidden field without that (an action) is
 * kept in the model but shows no chip until then.
 */
@Component({
  selector: 'dd-reveal-fields',
  template: `
    @for (entry of inlineEntries(); track $index) {
      <formly-field [field]="entry.field"></formly-field>
    }
    @for (entry of panelEntries(); track entry.key) {
      <dd-reveal-panel
        [open]="states()[entry.key] === 'editing'"
        [error]="emptySave()[entry.key] ? 'Vul dit veld in of kies Annuleren.' : null"
        [class.dd-reveal-folded]="states()[entry.key] !== 'editing'"
        (cancelEdit)="cancel(entry)"
        (saveEdit)="save(entry)">
        <formly-field [field]="entry.field"></formly-field>
        @for (rider of entry.riders; track rider) {
          <formly-field [field]="rider"></formly-field>
        }
      </dd-reveal-panel>
    }
    @if (!panelOpen()) {
      <dd-reveal-menu class="mt-2" [items]="items()" (pick)="pick($event)" (clear)="clear($event)" (edit)="edit($event)" />
    }
  `,
  styles: `
    /* A wrapping row, as route.component gives a stop's formly-group: the fields carry
       'md:w-1/2 float-left' classes and a float row breaks on the taller arrival time, so the
       widths divide the row as flex items instead. Menu and panel take a row of their own. */
    :host {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-start;
      align-content: flex-start;
      width: 100%;
    }
    dd-reveal-menu,
    dd-reveal-panel {
      display: block;
      flex-basis: 100%;
    }
    dd-reveal-panel {
      margin-top: 8px;
    }
    /* Figma: inside a stop card the menu sits 12px under the last field (the field's own 12px
       bottom padding already is that gap), and the card owns the 20px above its footer. */
    :host-context(.dd-stop-card__body) dd-reveal-menu {
      margin-top: 0 !important;
    }
    /* The panel's field fills the panel: the stop's 'md:w-1/2' / px width classes would stop it ~44px short. */
    dd-reveal-panel formly-field {
      display: block;
      width: 100% !important;
      max-width: none !important;
      float: none !important;
    }
    /* The linked-value field keeps an invisible 40px link button beside the textarea while it has no value;
       it is what left the panel textarea 44px short (the form-field is flex-1 next to it). A linked value keeps it. */
    dd-reveal-panel ::ng-deep formly-field-linked-value .opacity-0 {
      display: none !important;
    }
    /* A checkbox in the panel reads as the stop footer's (Figma): 16px, radius 2, 1px --dd-border-secondary on the card colour. */
    dd-reveal-panel ::ng-deep .mdc-checkbox {
      width: 16px;
      height: 16px;
      padding: 0;
      margin: 0 10px 0 0;
      flex: 0 0 16px;
    }
    dd-reveal-panel ::ng-deep .mdc-checkbox__background {
      top: 0;
      left: 0;
      width: 16px;
      height: 16px;
      border-radius: 2px;
      border-width: 1px;
    }
    dd-reveal-panel ::ng-deep .mat-mdc-checkbox:not(.mat-mdc-checkbox-checked) .mdc-checkbox__background {
      border-color: var(--dd-border-secondary) !important;
      background: var(--fuse-bg-card);
    }
    dd-reveal-panel ::ng-deep .mdc-checkbox .mdc-checkbox__native-control {
      top: -10px;
      left: -10px;
    }
    /* Figma: the photo requirement helper is 14px secondary text on one line. */
    dd-reveal-panel ::ng-deep mat-hint {
      font-size: 14px;
      line-height: 20px;
      color: var(--fuse-text-secondary);
    }
    /* Not [hidden]: tailwind important:true utilities on the children would win (see section). */
    .dd-reveal-folded {
      display: none !important;
    }
    :host(.dd-reveal-locked) ::ng-deep .dd-reveal-chip__clear,
    :host(.dd-reveal-locked) ::ng-deep .dd-reveal-add {
      display: none !important;
    }
  `,
  imports: [FormlyModule, RevealMenuComponent, RevealPanelComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.dd-reveal-locked]': 'allLocked()' },
})
export class RevealFieldsComponent implements OnInit, DoCheck {
  /**
   * Static per instance, like the section's props: the initial states, `resetOnHide` and the first
   * hide pass run once in ngOnInit. A config change rebuilds the form (and this component) anyway.
   */
  readonly fields = input<FormlyFieldConfig[]>([]);
  readonly revealKeys = input<string[]>([]);

  readonly #host = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly #injector = inject(Injector);

  readonly #states = signal<Record<string, RevealState>>({});
  protected readonly states = this.#states.asReadonly();
  /** Panels where Opslaan was pressed on an empty field (Figma: inline error, panel stays open). */
  readonly #emptySave = signal<Record<string, boolean>>({});
  protected readonly emptySave = this.#emptySave.asReadonly();
  /** What an open panel restores on Annuleren. */
  readonly #snapshots = new Map<string, Snapshot[]>();

  readonly #entries = computed<Entry[]>(() => {
    const keys = new Set(this.revealKeys());
    const entries = this.fields().map(field => {
      const key = String(field.key ?? '');
      // A nested key ('stop.note') may be listed by its own name.
      const listed = !!key && (keys.has(key) || keys.has(key.split('.').pop()!));
      // Its own hide condition would overrule the menu every pass (an empty panel that never saves);
      // a field the admin hid outright stays hidden. Both are left out.
      const ownHide = !!(field.hideExpression || field.expressions?.['hide'] || (field.hide === true && !revealHidden.has(field)));
      if (listed && ownHide && !warned.has(field)) {
        warned.add(field);
        console.warn(`[reveal] '${key}' has its own hide condition; left out of the menu`);
      }
      const reveal = listed && !ownHide && !roleHidden(field);
      return { field, key, reveal, panel: reveal && opensInPanel(field) && !holdsRepeat(field), host: null, riders: [] } as Entry;
    });
    // A rider rides in its host's panel and has no menu item of its own. Without a listed panel
    // host the prop is ignored and the field stays a normal reveal item (or inline field). A field
    // that rides itself is never a host: a chain (A on B, B on C) would leave A in no panel at all.
    // A repeat never rides: Annuleren cannot restore its rows (setValue throws once the row count
    // changed), so its revealWith is ignored and it stays a normal reveal item.
    const wantsHost = (entry: Entry) => !!entry.field.props?.['revealWith'] && !entry.field.fieldArray;
    for (const entry of entries) {
      const wanted = wantsHost(entry) ? (entry.field.props?.['revealWith'] as string) : undefined;
      const host = wanted && entry.reveal ? entries.find(other => other !== entry && other.panel && !wantsHost(other) && matches(other.key, wanted)) : undefined;
      if (!host) continue;
      entry.host = host.key;
      entry.panel = false;
      host.riders.push(entry.field);
    }
    return entries;
  });
  protected readonly inlineEntries = computed(() => this.#entries().filter(entry => !entry.panel && !entry.host));
  protected readonly panelEntries = computed(() => this.#entries().filter(entry => entry.panel));
  protected readonly panelOpen = computed(() => this.panelEntries().some(entry => this.#states()[entry.key] === 'editing'));

  /** Menu and chips follow the order of `revealKeys`, not the form's field order. */
  protected readonly items = computed<RevealItem[]>(() => {
    this.#controlState();
    const states = this.#states();
    const keys = this.revealKeys();
    const rank = (key: string) => {
      const i = keys.indexOf(key);
      return i >= 0 ? i : keys.indexOf(key.split('.').pop()!);
    };
    return this.#entries()
      // A locked field that is not on screen has nothing to show and may not be added.
      .filter(entry => entry.reveal && !entry.host && !(locked(entry.field) && states[entry.key] === 'hidden'))
      .sort((a, b) => rank(a.key) - rank(b.key))
      .map(entry => ({
        key: entry.key,
        label: entry.field.props?.label ?? entry.key,
        icon: (entry.field.props?.['revealIcon'] as string | undefined) ?? 'plus',
        shown: states[entry.key] !== 'hidden',
        // A filled value with no text to read out (only a photo flag) still needs a visible chip.
        chipValue: states[entry.key] === 'chip' ? chipText(entry.field.formControl?.value) || this.#riderLabel(entry) || 'ingevuld' : null,
        extraIcon: states[entry.key] === 'chip' ? this.#filledRider(entry)?.props?.['revealIcon'] : undefined,
        extraLabel: states[entry.key] === 'chip' ? this.#riderLabel(entry) || undefined : undefined,
        removable: !entry.panel && states[entry.key] === 'editing' && !locked(entry.field),
      }));
  });

  /** The model the states were derived from. A rebuild that hands this slot another stop's model changes it. */
  #model: unknown;
  /** Set once derived, so a model that is still undefined at init is re-derived when it arrives. */
  #derived = false;

  ngOnInit(): void {
    this.#derive();
  }

  /**
   * A reorder (`options.build` after a patchValue) keeps the field configs but moves the models
   * through them, so the states of a slot would describe the stop that used to sit there. A new
   * model identity re-derives them; the same stop keeps its state (an open, still empty panel stays open).
   */
  ngDoCheck(): void {
    if (this.#derived && this.#modelOf() !== this.#model) this.#derive();
    if (this.#derived) this.#followSelfFilled();
    if (this.#derived) this.#followControls();
  }

  /**
   * Chip texts and the locked flag read the controls, which are no signals: a value that changes
   * under a chip (a linked note following its address) or a role lock re-runs `items` through this.
   */
  readonly #controlState = signal('');
  #followControls(): void {
    const states = this.#states();
    const parts = this.#entries().map(entry =>
      `${locked(entry.field)}:${states[entry.key] === 'chip' ? chipText(entry.field.formControl?.value) : ''}`
    );
    this.#controlState.set(parts.join('\u0000'));
  }

  /** Every field locked: no × on the chips and no "Toevoegen" (the menu has nothing left to do). */
  protected readonly allLocked = computed(() => {
    this.#controlState();
    const entries = this.#entries().filter(entry => entry.reveal && !entry.host);
    return entries.length > 0 && entries.every(entry => locked(entry.field));
  });

  /** A self-filling field that fills or empties on its own (a picked address) gets or drops its chip. */
  #followSelfFilled(): void {
    const states = this.#states();
    for (const entry of this.#entries()) {
      if (!entry.panel || entry.host || !fillsItself(entry.field) || states[entry.key] === 'editing') continue;
      const filled = this.#filled(entry);
      if (filled === (states[entry.key] === 'chip')) continue;
      entry.riders.forEach(rider => this.#setHidden(rider, !filled));
      this.#setState(entry.key, filled ? 'chip' : 'hidden');
    }
  }

  #modelOf(): unknown {
    const first = this.fields()[0];
    return first?.parent?.model ?? first?.model;
  }

  /** Hydration, not a user edit: reads the model (a hidden field has no control) and never dirties. */
  #derive(): void {
    this.#model = this.#modelOf();
    this.#derived = true;
    this.#snapshots.clear();
    this.#emptySave.set({});
    const states: Record<string, RevealState> = {};
    for (const entry of this.#entries()) {
      if (!entry.reveal || entry.host) continue;
      // Hiding a filled field must never wipe it.
      entry.field.resetOnHide = false;
      states[entry.key] = this.#storedFilled(entry) ? (entry.panel ? 'chip' : 'editing') : 'hidden';
      this.#setHidden(entry.field, states[entry.key] === 'hidden');
      for (const rider of entry.riders) {
        rider.resetOnHide = false;
        this.#setHidden(rider, states[entry.key] === 'hidden');
      }
    }
    this.#states.set(states);
  }

  /** `#filled` for a field that may be hidden: the model, not the control. */
  #storedFilled(entry: Entry): boolean {
    return [entry.field, ...entry.riders].some(field => isFilled(getFieldValue(field)));
  }

  pick(key: string): void {
    const entry = this.#entry(key);
    if (!entry || locked(entry.field)) return;
    // Unhide first: a hidden group has no child controls yet, so its snapshot would be empty.
    this.#setHidden(entry.field, false);
    entry.riders.forEach(rider => this.#setHidden(rider, false));
    this.#snapshot(entry);
    this.#setState(key, 'editing');
  }

  edit(key: string): void {
    const entry = this.#entry(key);
    if (!entry || locked(entry.field)) return;
    this.#snapshot(entry);
    this.#setState(key, 'editing');
  }

  save(entry: Entry): void {
    if (!this.#filled(entry)) {
      // Not setErrors on the control: an optional note would then also show the field's own
      // "verplicht" error. The panel shows the message and tints the field (reveal-panel CSS).
      this.#emptySave.update(keys => ({ ...keys, [entry.key]: true }));
      return;
    }
    this.#snapshots.delete(entry.key);
    this.#settle(entry);
  }

  cancel(entry: Entry): void {
    for (const { control, value, pristine } of this.#snapshots.get(entry.key) ?? []) {
      control.setValue(complete(control, value));
      if (pristine) control.markAsPristine();
    }
    this.#snapshots.delete(entry.key);
    this.#settle(entry);
  }

  /**
   * The × on a chip, or unchecking an inline field in the menu: empties the field as a user edit
   * (the form goes dirty) and puts it back behind the menu.
   */
  clear(key: string): void {
    const entry = this.#entry(key);
    const control = entry?.field.formControl;
    if (!entry || !control || locked(entry.field)) return;
    // Not reset(): Formly's controls reset to the value they were built with, i.e. the old note.
    // An inline field taken away untouched changed nothing, so it must not dirty the form.
    if (isFilled(control.value)) {
      if (entry.field.fieldArray) {
        // A repeat empties the way FieldArrayType.remove does: the model rows go, then a rebuild
        // drops their fields and controls. FormArray.clear() alone would leave the rows in
        // fieldGroup, and the next unhide would register them again with their old values.
        // ɵgetFieldValue: private Formly export (read from 7.1.0); the "take away, pick again" spec
        // is the tripwire if it moves.
        const rows = getFieldValue(entry.field);
        if (Array.isArray(rows)) rows.length = 0;
        entry.field.options?.build?.(entry.field);
        markUserEdit(control);
      } else if (control instanceof FormArray) {
        control.clear();
        markUserEdit(control);
      } else {
        commitUserValue(control, blank(control));
      }
    }
    for (const rider of entry.riders) {
      const riderControl = rider.formControl;
      if (riderControl && isFilled(riderControl.value)) commitUserValue(riderControl, blank(riderControl));
      this.#setHidden(rider, true);
    }
    this.#setHidden(entry.field, true);
    this.#setState(key, 'hidden');
  }

  /** After a panel closes: a filled field becomes a chip, an empty one goes back behind the menu. */
  #settle(entry: Entry): void {
    this.#emptySave.update(keys => ({ ...keys, [entry.key]: false }));
    const filled = this.#filled(entry);
    this.#setHidden(entry.field, !filled);
    entry.riders.forEach(rider => this.#setHidden(rider, !filled));
    this.#setState(entry.key, filled ? 'chip' : 'hidden');
    // The panel took the menu's place, so focus went with it: hand it back to the chip or "Toevoegen".
    afterNextRender(
      () => {
        const host = this.#host.nativeElement;
        const target = host.querySelector<HTMLElement>(`[data-cy="reveal-chip-${entry.key}"] button`) ?? host.querySelector<HTMLElement>('[data-cy="reveal-add"]');
        target?.focus();
      },
      { injector: this.#injector },
    );
  }

  #snapshot(entry: Entry): void {
    const controls = [entry.field, ...entry.riders].map(field => field.formControl as AbstractControl | undefined).filter((control): control is AbstractControl => !!control);
    // getRawValue: disabled children too, so the restore gets every child it needs.
    this.#snapshots.set(
      entry.key,
      controls.map(control => ({ control, value: structuredClone(control.getRawValue() ?? blank(control)), pristine: control.pristine })),
    );
  }

  /** Host or any rider carries a value: only a rider set (a tick, no text) still counts. */
  #filled(entry: Entry): boolean {
    return isFilled(entry.field.formControl?.value) || !!this.#filledRider(entry);
  }

  #filledRider(entry: Entry): FormlyFieldConfig | undefined {
    return entry.riders.find(rider => isFilled(rider.formControl?.value));
  }

  #riderLabel(entry: Entry): string {
    return String(this.#filledRider(entry)?.props?.label ?? '');
  }

  #entry(key: string): Entry | undefined {
    return this.#entries().find(entry => entry.reveal && entry.key === key);
  }

  #setState(key: string, state: RevealState): void {
    this.#states.update(states => ({ ...states, [key]: state }));
  }

  /** Formly applies `hide` (and the control (un)registration with it) on its next expression pass. */
  #setHidden(field: FormlyFieldConfig, hide: boolean): void {
    if (hide && fillsItself(field)) hide = false;
    if (!!field.hide === hide) return;
    field.hide = hide;
    revealHidden.add(field);
    let root = field;
    while (root.parent) root = root.parent;
    field.options?.checkExpressions?.(root);
  }
}
